import {
  Button, Loading, useRecordContext, useTranslate, useCreatePath,
  Link,
  useGetList,
} from 'react-admin';
import {
  DialogTitle, DialogContent, Dialog,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  DialogActions,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Chip,
  Stack,
  TableContainer,
} from '@mui/material';
import { useState, useMemo } from 'react';
import { Asset, CertificationCategory, CertificationProperties } from '@x-guard/xgac-types/xgac';
import { CertificationExpiryWithDaysCaption } from '../CertificationExpiryWithDaysCaption';
import { getExpiryStatus } from '../../utils/certificationsAndItemsExpiry';
import type { ExpiryStatus } from '../../utils/certificationsAndItemsExpiry';
import { RaXgacRecord } from '../../lib/RaXgacRecord';

type CertifiedUserInfo = {
  _id: string;
  name: string;
  firstIssuedOn: string;
  lastReceivedOn: string;
  expiryDate: string;
  type: ExpiryStatus;
};

const FILTER_OPTIONS: { label: string; value: ExpiryStatus }[] = [
  { label: 'resources.certification-categories.text.certified', value: 'valid' },
  { label: 'resources.certification-categories.text.expiring', value: 'expiring' },
  { label: 'resources.certification-categories.text.expired', value: 'expired' },
];

const getUsersForTypes = (
  selectedTypes: ExpiryStatus[],
  valid: CertifiedUserInfo[],
  expiring: CertifiedUserInfo[],
  expired: CertifiedUserInfo[],
): CertifiedUserInfo[] => {

  const byType: Record<ExpiryStatus, CertifiedUserInfo[]> = {
    valid,
    expiring,
    expired,
  };

  const existing = new Set<string>();

  return selectedTypes.flatMap((selectedType) => byType[selectedType].filter((user) => {

    if (existing.has(user._id)) {

      return false;

    }
    existing.add(user._id);
    return true;

  }));

};

const UsersDialog = (props: {
  open: boolean;
  onClose: () => void;
  types: ExpiryStatus[];
  expiringCertificationsInfo: CertifiedUserInfo[];
  expiredCertificationsInfo: CertifiedUserInfo[];
  validCertificationsInfo: CertifiedUserInfo[];
  certificationCategoryName: string;
  largestReminderPeriodInDays: number;
  loading: boolean;
  setUsersDialogTypes: (types: ExpiryStatus[]) => void;
}) => {

  const {
    open, onClose, types, expiringCertificationsInfo,
    expiredCertificationsInfo, validCertificationsInfo, certificationCategoryName, largestReminderPeriodInDays,
    loading,
    setUsersDialogTypes,
  } = props;
  const translate = useTranslate();
  const createPath = useCreatePath();

  const handleFilterChange = (type: ExpiryStatus, checked: boolean) => {

    const nextTypes = FILTER_OPTIONS
      .map((option) => option.value)
      .filter((value) => (value === type ? checked : types.includes(value)));

    setUsersDialogTypes(
      nextTypes.length > 0
        ? nextTypes
        : FILTER_OPTIONS.map((option) => option.value),
    );

  };

  const selectedTypes: ExpiryStatus[] = types.length > 0 ? types : ['valid', 'expiring', 'expired'];
  const certificationsInfo = getUsersForTypes(
    selectedTypes,
    validCertificationsInfo,
    expiringCertificationsInfo,
    expiredCertificationsInfo,
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {`${translate('resources.certification-categories.text.users_dialog_title')} "${certificationCategoryName}"`}
      </DialogTitle>
      <DialogContent sx={{ minHeight: '300px' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            minHeight: '48px',
            px: 2,
            py: 0.5,
            borderRadius: '8px',
            backgroundColor: '#F0F9F5',
          }}
        >
          <Typography sx={{ mr: 1 }}>
            {translate('resources.certification-categories.text.filter_by')}
          </Typography>

          {FILTER_OPTIONS.map((option) => (
            <FormControlLabel
              key={option.value}
              label={translate(option.label)}
              sx={{ m: 0, mr: 2 }}
              control={(
                <Checkbox
                  size="small"
                  checked={types.includes(option.value)}
                  onChange={(_, checked) => handleFilterChange(option.value, checked)}
                />
              )}
            />
          ))}
        </Box>
        {loading && (
          <Loading loadingPrimary={translate('general.text.loading')} loadingSecondary={translate('general.text.loading_secondary')}/>
        )}
        {!loading && certificationsInfo.length === 0 && (
          <Typography sx={{ textAlign: 'center', marginTop: '20px' }}>
            {translate('resources.certification-categories.text.users_dialog_empty')}
          </Typography>
        )}
        {!loading && certificationsInfo.length > 0 && (
          <TableContainer className="bhvk-table">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{translate('general.fields.name')}</TableCell>
                  <TableCell>{translate('resources.assets.certifications.fields.first_issued_on')}</TableCell>
                  <TableCell>{translate('resources.assets.certifications.fields.last_received_on')}</TableCell>
                  <TableCell>{translate('resources.assets.certifications.fields.expiry_date')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {certificationsInfo.map((asset) => (
                  <TableRow key={asset._id}>
                    <TableCell>
                      <Link
                        to={createPath({ resource: 'assets', type: 'edit', id: asset._id })}
                      >{asset.name}
                      </Link>
                    </TableCell>
                    <TableCell>{new Date(asset.firstIssuedOn).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(asset.lastReceivedOn).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <CertificationExpiryWithDaysCaption
                        record={{
                          ...asset,
                          id: asset._id,
                          largestReminderPeriodInDays,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button className="BhvkCancelButton-root" size="medium" onClick={onClose} label={translate('general.text.close')}/>
      </DialogActions>
    </Dialog>
  );

};

// disabled because the label is simply added to display the column name in the datagrid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const CertifiedUsersButton = (props: { label?: string }) => {

  const record = useRecordContext<RaXgacRecord<CertificationCategory>>();
  const translate = useTranslate();

  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [usersDialogTypes, setUsersDialogTypes] = useState<ExpiryStatus[]>(['valid']);

  const { data: assets = [], isLoading: loading } = useGetList<RaXgacRecord<Asset>>(
    'all-assets',
    {
      pagination: { page: 1, perPage: -1 },
      sort: { field: 'name', order: 'ASC' },
      filter: { 'certifications.certificationCategory._id': record?._id },
    },
    { enabled: Boolean(record?._id) },
  );

  const certifiedUsers = useMemo((): CertifiedUserInfo[] => {

    if (!record?._id) return [];
    return assets.map((asset) => {

      const certificationOfCategory = (asset.certifications ?? [])
        .find((certification: CertificationProperties) => certification.certificationCategory._id === record?._id);
      const firstIssuedOn = certificationOfCategory?.firstIssuedOn || '';
      const lastReceivedOn = certificationOfCategory?.lastReceivedOn || '';
      const expiryDate = certificationOfCategory?.expiryDate || '';
      const type = getExpiryStatus(expiryDate, record?.largestReminderPeriodInDays ?? 0);

      return {
        _id: asset._id,
        name: asset.name,
        firstIssuedOn,
        lastReceivedOn,
        expiryDate,
        type,
      };

    });

  }, [assets, record]);

  const validCertifications = useMemo(
    () => certifiedUsers.filter((user) => user.type === 'valid'),
    [certifiedUsers],
  );

  const expiringCertifications = useMemo(
    () => certifiedUsers.filter((user) => user.type === 'expiring'),
    [certifiedUsers],
  );

  const expiredCertifications = useMemo(
    () => certifiedUsers.filter((user) => user.type === 'expired'),
    [certifiedUsers],
  );

  return (
    <>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          whiteSpace: 'nowrap',
          '& .MuiChip-root': {
            minWidth: 100,
          },
        }}
      >
        <Chip
          disabled={validCertifications.length === 0}
          onClick={() => {

            setUsersDialogOpen(true);
            setUsersDialogTypes(['valid']);

          }}
          label={`${validCertifications.length} ${translate('resources.certification-categories.text.certified')}`}
        />
        <Chip
          disabled={expiringCertifications.length === 0}
          onClick={() => {

            setUsersDialogOpen(true);
            setUsersDialogTypes(['expiring']);

          }}
          label={`${expiringCertifications.length} ${translate('resources.certification-categories.text.expiring')}`}
          sx={{ color: 'warning.main' }}
        />
        <Chip
          disabled={expiredCertifications.length === 0}
          onClick={() => {

            setUsersDialogOpen(true);
            setUsersDialogTypes(['expired']);

          }}
          label={`${expiredCertifications.length} ${translate('resources.certification-categories.text.expired')}`}
          sx={{ color: 'error.main' }}
        />
      </Stack>
      <UsersDialog
        open={usersDialogOpen}
        onClose={() => setUsersDialogOpen(false)}
        types={usersDialogTypes}
        expiringCertificationsInfo={expiringCertifications}
        expiredCertificationsInfo={expiredCertifications}
        validCertificationsInfo={validCertifications}
        certificationCategoryName={record?.name ?? ''}
        largestReminderPeriodInDays={record?.largestReminderPeriodInDays ?? 0}
        loading={loading}
        setUsersDialogTypes={setUsersDialogTypes}
      />
    </>
  );

};
